'use client';

import React, { createContext, useEffect, useState, ReactNode } from 'react';
import { RegionalSettings, RegionalContext as IRegionalContext } from '@/types/regional';
import { RegionalDetector } from '@/utils/regionalDetector';
import { RegionalFormatter } from '@/utils/regionalFormatter';
import { I18n } from '@/utils/i18n';

const RegionalContext = createContext<IRegionalContext | null>(null);

interface RegionalProviderProps {
  children: ReactNode;
  initialSettings?: Partial<RegionalSettings>;
}

export const RegionalProvider: React.FC<RegionalProviderProps> = ({ 
  children, 
  initialSettings 
}) => {
  const [settings, setSettings] = useState<RegionalSettings | null>(null);
  const [formatter, setFormatter] = useState<RegionalFormatter | null>(null);
  const [i18n, setI18n] = useState<I18n | null>(null);

  useEffect(() => {
    // Detect regional settings on mount
    const detection = RegionalDetector.detect();
    const detectedSettings = RegionalDetector.createSettings(detection);
    
    // Merge with any initial settings provided
    const finalSettings: RegionalSettings = {
      ...detectedSettings,
      ...initialSettings
    };

    setSettings(finalSettings);
    setFormatter(new RegionalFormatter(finalSettings));
    setI18n(new I18n(finalSettings.locale));

    // Store in localStorage for persistence
    localStorage.setItem('regionalSettings', JSON.stringify(finalSettings));
  }, [initialSettings]);

  // Load from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('regionalSettings');
    if (stored && !initialSettings) {
      try {
        const storedSettings = JSON.parse(stored) as RegionalSettings;
        setSettings(storedSettings);
        setFormatter(new RegionalFormatter(storedSettings));
        setI18n(new I18n(storedSettings.locale));
      } catch (error) {
        console.warn('Failed to parse stored regional settings:', error);
      }
    }
  }, []);

  if (!settings || !formatter || !i18n) {
    // Provide default context during loading
    const defaultContext: IRegionalContext = {
      settings: {
        locale: 'en-US',
        timezone: 'UTC',
        currency: 'USD',
        dateFormat: 'MM/dd/yyyy',
        timeFormat: '12h',
        numberFormat: 'US',
        firstDayOfWeek: 0
      },
      formatDate: (date: Date) => date.toLocaleDateString('en-US'),
      formatTime: (date: Date) => date.toLocaleTimeString('en-US'),
      formatDateTime: (date: Date) => date.toLocaleString('en-US'),
      formatCurrency: (amount: number) => `$${amount.toFixed(2)}`,
      formatNumber: (num: number) => num.toLocaleString('en-US'),
      formatFileSize: (bytes: number) => `${(bytes / 1024 / 1024).toFixed(2)} MB`,
      formatPercentage: (value: number) => `${value.toFixed(1)}%`,
      getTimezone: () => 'UTC',
      getUserTimezone: () => 'UTC',
      convertToUserTimezone: (date: Date) => date,
      isRTL: () => false,
      t: (key: string) => key
    };
    
    return (
      <RegionalContext.Provider value={defaultContext}>
        {children}
      </RegionalContext.Provider>
    );
  }

  const contextValue: IRegionalContext = {
    settings,
    formatDate: (date: Date) => formatter.formatDate(date),
    formatTime: (date: Date) => formatter.formatTime(date),
    formatDateTime: (date: Date) => formatter.formatDateTime(date),
    formatCurrency: (amount: number) => formatter.formatCurrency(amount),
    formatNumber: (num: number) => formatter.formatNumber(num),
    formatFileSize: (bytes: number) => formatter.formatFileSize(bytes),
    formatPercentage: (value: number) => formatter.formatPercentage(value),
    getTimezone: () => formatter.getTimezone(),
    getUserTimezone: () => formatter.getUserTimezone(),
    convertToUserTimezone: (date: Date) => formatter.convertToUserTimezone(date),
    isRTL: () => formatter.isRTL(),
    t: (key: string, params?: Record<string, any>) => i18n.t(key, params),
    formatter: {
      validateCronField: (field: string, min: number, max: number) => formatter.validateCronField(field, min, max),
      validateCronExpression: (cron: string) => formatter.validateCronExpression(cron),
      validatePhoneNumber: (phone: string) => formatter.validatePhoneNumber(phone),
      sortCloudRegionsByProximity: (regions: string[]) => formatter.sortCloudRegionsByProximity(regions),
      validateDataResidency: (region: string, storageRegion: string) => formatter.validateDataResidency(region, storageRegion)
    }
  };

  return (
    <RegionalContext.Provider value={contextValue}>
      <div dir={formatter.isRTL() ? 'rtl' : 'ltr'}>
        {children}
      </div>
    </RegionalContext.Provider>
  );
};

export { RegionalContext };